import React, { useEffect, useMemo, useState } from 'react';
import StudyProgramCard from '@/components/molecules/de-uni/study-program-card/study-program-card';
import { ICourse } from '@/models/ICourse';
// import { HeapEventName } from '@/constants/heap-event-name';
// import { SearchHeapService } from '@/services/search-heap.service';
import {
  SearchHookProps,
  useSearch,
  RichText,
  IToggleButton,
  ToggleButtonGroup,
  SearchBar,
  Button,
  ButtonSizeType,
  ButtonStyleType,
  ApplicationType,
  Dropdown,
  getSearchQuery,
} from '@into-discovery/browsewebshared';
import { defaultSearchParams } from '@/constants/default-search-params';
import { IFacet } from '@into-discovery/browsewebshared';
import IRichText from '@into-discovery/browsewebshared/src/models/viewModels/base/IRichText';
import colorStyles from '@/styles/utils/variables.module.scss';
import { useLocale } from 'next-intl';
import { appBranding } from '@/data/application-branding';
import styles from './study-programs.module.scss';

type Props = {
  studyPrograms: ICourse[];
  searchBrandId: string;
  universityPrimaryColor: string;
  totalStudyPrograms: number;
  studyProgramsIntro: IRichText;
};

const appSettings = {
  googleApiKey: process.env.MAPS_API_KEY,
  branding: appBranding,
  b2cFlag: true,
  applicationType: ApplicationType.IntoStudy,
};

const sortOrderOptions = [
  { text: `Best Match`, value: `1` },
  { text: `Sort by program A-Z`, value: `4` },
  { text: `Sort by program Z-A`, value: `5` },
];

export default function StudyPrograms({
  studyPrograms,
  searchBrandId,
  universityPrimaryColor,
  totalStudyPrograms,
  studyProgramsIntro,
}: Props) {
  const [programs, setPrograms] = useState<ICourse[]>(studyPrograms);
  const [totalPrograms, setTotalPrograms] = useState<number>(totalStudyPrograms);
  const currentLocale = useLocale();

  const directEntryFacet: IFacet = {
    id: `entryRoutes`,
    title: `Entry Routes`,
    values: [`Direct Entry`],
  };

  const brandFacet: IFacet = {
    id: `brandId`,
    title: `Brand Id`,
    values: [searchBrandId],
  };
  const defaultFacets: IFacet[] = [directEntryFacet, brandFacet];

  const [searchParams, setSearchParams] = useState<SearchHookProps>({
    ...defaultSearchParams,
    sortOrder: `4`,
    facets: defaultFacets,
  });

  function programLevelChangeHandler(val) {
    const facets: IFacet[] = [
      directEntryFacet,
      brandFacet,
      ...(val
        ? [
            {
              id: `studyLevel`,
              title: `Study Level`,
              values: val,
            },
          ]
        : []),
    ];
    setSearchParams({ ...searchParams, facets, page: 1 });
  }

  const programLevelRadioButtonParams: IToggleButton[] = [
    {
      label: `Any`,
      value: ``,
      group: `studyProgramsGroup`,
      handleOnToggle: programLevelChangeHandler,
      isChecked: true,
      inputColor: colorStyles.primary,
    },
    {
      label: `Undergraduate`,
      value: `Undergraduate`,
      group: `studyProgramsGroup`,
      handleOnToggle: programLevelChangeHandler,
      isChecked: false,
      inputColor: colorStyles.primary,
    },
    {
      label: `Graduate`,
      value: `Graduate`,
      group: `studyProgramsGroup`,
      handleOnToggle: programLevelChangeHandler,
      isChecked: false,
      inputColor: colorStyles.primary,
    },
  ];

  // function trackHeapRequest(studyProgramsData) {
  //   const { searchOptions } = studyProgramsData;
  //   if (searchOptions && Object.keys(searchOptions)?.length > 0) {
  //     SearchHeapService.trackSearchRequest(searchOptions, HeapEventName.ViewStudyPrograms);
  //   }
  // }

  const searchOptions = useMemo(() => {
    const query: SearchHookProps = {
      ...defaultSearchParams,
      facets: defaultFacets,
    };

    return {
      searchQuery: getSearchQuery(query),
      isNextPageAvailable:
        totalPrograms / defaultSearchParams.pageSize >
        (searchParams.page ?? defaultSearchParams.page),
    };
  }, [defaultFacets, searchParams.page, totalPrograms]);

  const { data, isLoading } = useSearch(
    searchOptions.searchQuery,
    defaultSearchParams.searchApiSettings,
    null,
    null,
    [searchOptions.searchQuery],
  );

  useEffect(() => {
    if (data) {
      const searchData = (data as any).data;

      if (searchParams.page === 1) {
        setPrograms(searchData.results);
      } else {
        setPrograms([...programs, ...searchData.results]);
      }
      //trackHeapRequest(data);
      setTotalPrograms(searchData.searchOptions.pagingInfo.total);
    }
  }, [data]);

  const orderChangeHandler = (val) => {
    setSearchParams({ ...searchParams, sortOrder: val, page: 1 });
  };

  // The onSearch is removed in this refactoring https://iup2.visualstudio.com/Into.Discovery/_git/Into.Discovery.BrowseWebShared/pullrequest/22841?path=/src/components/molecules/search-bar/search-bar.tsx
  // Won't fix now, because the code looks dead
  // const onSearch = (val) => {
  //   setSearchParams({ ...searchParams, keyword: val, page: 1 });
  // };

  const showMoreHandler = () => {
    if (!isLoading) {
      setSearchParams({
        ...searchParams,
        page: (searchParams.page ?? defaultSearchParams.page) + 1,
      });
    }
  };

  return (
    <section className="section-separator" id="study-programs">
      <h2 className="mb-2">Study programs</h2>
      {studyProgramsIntro && (
        <div className="mb-4">
          <RichText richText={studyProgramsIntro} />
        </div>
      )}
      <div className={`mb-3 ${styles[`study-programs-input`]}`}>
        <SearchBar
          cssWrapper="header"
          customClassName="study-programs-search"
          acceptEmptyValue
          idPrefix="programs-search"
          typeAheadPlaceHolder="Search INTO's programs"
          applicationSettings={appSettings}
          typeaheadActive={false}
          currentLocale={currentLocale}
        />
      </div>
      <div className="row">
        <div className="col-sm-12 col-md-auto mb-3">
          <ToggleButtonGroup toggleButtonProps={programLevelRadioButtonParams} />
        </div>
        <div className={`${styles[`dropdown-wrapper`]} col-auto mb-3 me-4 me-sm-0`}>
          <Dropdown
            onChange={orderChangeHandler}
            options={sortOrderOptions}
            defaultValue={searchParams.sortOrder}
            ariaLabel="Order dropdown"
            id="select"
          />
        </div>
      </div>
      {programs &&
        programs.map((program) => (
          <StudyProgramCard
            key={program.mainProgram.id}
            studyProgram={program}
            brandColor={universityPrimaryColor}
          />
        ))}
      {!programs?.length ? <p>No programs found</p> : null}
      {programs?.length > 0 && (
        <div className={`${styles[`study-programs-footer`]} mt-3`}>
          {searchOptions.isNextPageAvailable && (
            <Button
              buttonID="showMore"
              buttonAriaLabel="Show more programs"
              buttonName="showMore"
              buttonStyle={ButtonStyleType.Outline}
              buttonSize={ButtonSizeType.Medium}
              copyColor={colorStyles.primary}
              outlineColor={colorStyles.primary}
              backgroundColor={colorStyles.white}
              hoverBackgroundColor={colorStyles.primaryHover}
              activeBackgroundColor={colorStyles.primaryActive}
              onClick={showMoreHandler}
              buttonRole="button"
            >
              Show more programs
            </Button>
          )}
          {!isLoading && (
            <div className={`${styles[`number-of-elements`]} pe-3 text-end`}>
              {`Showing ${programs.length} of ${totalPrograms}`}
            </div>
          )}
        </div>
      )}
    </section>
  );
}
